import * as monaco from "monaco-editor"
import htmx from "htmx.org"
import { getFormCurrentValues, getFormInitialValues } from "./form"

declare global {
  interface Window {
    MonacoEditorManager: MonacoEditorManager
  }
}

class MonacoEditor {
  editor: monaco.editor.IStandaloneCodeEditor | null = null
  hiddenInput: HTMLTextAreaElement | null = null
  formId: string | null = null
  minHeight: number
  private containerRef: Element | null = null
  private autoSubmitTimer: ReturnType<typeof setTimeout> | null = null

  constructor(private elementId: string) {
    this.minHeight = 100
  }

  markAsTouched(): void {
    const element = document.getElementById(this.elementId)
    if (!element) {
      return
    }

    if (!element.hasAttribute("data-touched")) {
      element.setAttribute("data-touched", "true")
    }
  }

  private resizeCleanup: (() => void) | null = null

  initResizeHandle(container: Element): void {
    const handle = container.querySelector<HTMLElement>("[data-monaco-resize-handle]")
    if (!handle) {
      return
    }

    let startY = 0
    let startHeight = 0

    const onMouseMove = (e: MouseEvent) => {
      const newHeight = startHeight + (e.clientY - startY)
      ;(container as HTMLElement).style.height = `${Math.max(this.minHeight, newHeight)}px`
    }

    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove)
      document.removeEventListener("mouseup", onMouseUp)
    }

    const onMouseDown = (e: MouseEvent) => {
      e.preventDefault()
      startY = e.clientY
      startHeight = (container as HTMLElement).offsetHeight
      document.addEventListener("mousemove", onMouseMove)
      document.addEventListener("mouseup", onMouseUp)
    }

    handle.addEventListener("mousedown", onMouseDown)
    this.resizeCleanup = () => {
      handle.removeEventListener("mousedown", onMouseDown)
      document.removeEventListener("mousemove", onMouseMove)
      document.removeEventListener("mouseup", onMouseUp)
    }
  }

  init(): void {
    const element = document.getElementById(this.elementId)
    if (!element) {
      return
    }

    const container = element.closest("[data-monaco-editor-container]")
    if (!container) {
      return
    }

    this.containerRef = container
    container.setAttribute("data-monaco-initialized", "true")

    if (container.hasAttribute("data-resizable")) {
      this.initResizeHandle(container)
    }

    const inputName = element.getAttribute("data-monaco-editor-input-name")

    const form = element.closest("form")

    const hiddenInputForInit = form?.querySelector<HTMLTextAreaElement>(`textarea[name="${inputName}"][data-monaco-editor-hidden-input]`)
    let initialContent = hiddenInputForInit?.value ?? element.getAttribute("data-initial-content") ?? ""

    if (form && inputName) {
      try {
        const initialValues = getFormInitialValues(form)
        const currentValues = getFormCurrentValues(initialValues, form)

        if (currentValues[inputName] != null) {
          initialContent = currentValues[inputName].toString()
        }
      } catch {}
    }

    const readonly = element.getAttribute("data-readonly") === "true"
    const language = element.getAttribute("data-language")
    const theme = element.getAttribute("data-theme")

    this.formId = form?.id || null

    if (this.editor) {
      this.destroy()
    }

    const consumeMouseWheel = element.hasAttribute("data-monaco-editor-consume-mouse-wheel")

    this.editor = monaco.editor.create(element, {
      value: initialContent,
      ...language && { language },
      readOnly: readonly,
      automaticLayout: true,
      ...theme && { theme },
      minimap: { enabled: false },
      wordWrap: "on",
      scrollbar: {
        alwaysConsumeMouseWheel: consumeMouseWheel,
      },
    })

    requestAnimationFrame(() => {
      this.editor?.layout()
    })

    this.editor.onDidChangeModelContent(() => {
      this.markAsTouched()
      this.syncWithHiddenInput()
      this.clearEditorError()

      if (this.formId && window.onFormUpdate) {
        window.onFormUpdate(this.formId)
      }
    })

    if (inputName && form) {
      this.hiddenInput = form.querySelector(`textarea[name="${inputName}"]`) as HTMLTextAreaElement

      this.syncWithHiddenInput()
    }
  }

  syncWithHiddenInput(): void {
    if (this.hiddenInput && this.editor) {
      this.hiddenInput.value = this.editor.getValue()
      this.hiddenInput.dispatchEvent(new Event("input", { bubbles: true }))
    }
  }

  setContent(content: string): void {
    if (this.editor) {
      this.editor.setValue(content)
      this.syncWithHiddenInput()
    }
  }

  onReset(): void {
    const element = document.getElementById(this.elementId)
    if (!element) {
      return
    }
    
    const form = element.closest("form")
    const inputName = element.getAttribute("data-monaco-editor-input-name")
    if (!form || !inputName) {
      return
    }

    const hiddenInputForInit = form.querySelector<HTMLTextAreaElement>(`textarea[name="${inputName}"][data-monaco-editor-hidden-input]`)
    let initialContent = hiddenInputForInit?.value ?? element.getAttribute("data-initial-content") ?? ""

    try {
      const initialValues = getFormInitialValues(form)
      const currentValues = getFormCurrentValues(initialValues, form)

      if (currentValues[inputName] != null) {
        initialContent = currentValues[inputName].toString()
      }
    } catch {}

    this.clearEditorError()

    this.setContent(initialContent)
  }

  clearEditorError(): void {
    const element = document.getElementById(this.elementId)
    if (!element) {
      return
    }

    const container = element.closest("[data-monaco-editor-container]")
    if (!container) {
      return
    }

    const hasError = container.classList.contains("!border-red-500")
    if (hasError) {
      container.classList.remove("!border-red-500")

      const caption = container.parentElement?.querySelector(".textarea__caption")
      if (caption && caption.classList.contains("!text-red-600")) {
        caption.remove()
      }
    }
  }

  getContent(): string {
    return this.editor ? this.editor.getValue() : ""
  }

  getEditor(): monaco.editor.IStandaloneCodeEditor | null {
    return this.editor
  }

  setReadOnly(readOnly: boolean): void {
    if (this.editor) {
      this.editor.updateOptions({ readOnly })
    }
  }

  dispose(): void {
    if (this.autoSubmitTimer !== null) {
      clearTimeout(this.autoSubmitTimer)
      this.autoSubmitTimer = null
    }
    this.resizeCleanup?.()
    this.resizeCleanup = null
    if (this.editor) {
      this.editor.dispose()
      this.editor = null

      // Use the stored container reference rather than re-querying by ID,
      // to avoid accidentally targeting a newly-swapped-in element with the same ID
      this.containerRef?.removeAttribute("data-monaco-initialized")
      this.containerRef = null
    }
  }

  destroy(): void {
    if (this.autoSubmitTimer !== null) {
      clearTimeout(this.autoSubmitTimer)
      this.autoSubmitTimer = null
    }
    this.resizeCleanup?.()
    this.resizeCleanup = null
    if (this.editor) {
      const element = document.getElementById(this.elementId)
      if (element) {
        element.innerHTML = ""
      }
      this.editor.dispose()
      this.editor = null
      this.containerRef = null
    }
  }
}

export default class MonacoEditorManager {
  private editors: Record<string, MonacoEditor> = {}

  constructor() {
    window.MonacoEditorManager = this
  }

  init(): void {
    const elements = document.querySelectorAll("[data-monaco-editor-container]:not([data-monaco-initialized])")
    elements.forEach((element) => {
      const elementId = element.getAttribute("data-monaco-editor-id")
      if (elementId) {
        try {
          this.registerEditor(elementId)
        } catch (e) {
          console.error(`MonacoEditorManager: failed to initialize editor "${elementId}"`, e)

          element.removeAttribute("data-monaco-initialized")
        }
      }
    })

    // Clean up stale editor instances whose DOM elements are no longer in the document
    for (const elementId of Object.keys(this.editors)) {
      if (!document.getElementById(elementId)) {
        this.editors[elementId].dispose()
        delete this.editors[elementId]
      }
    }
  }

  registerEditor(elementId: string): void {
    if (this.editors[elementId]) {
      this.editors[elementId].dispose()
      delete this.editors[elementId]
    }
    const editor = new MonacoEditor(elementId)
    editor.init()
    this.editors[elementId] = editor
  }

  getEditor(elementId: string): MonacoEditor | null {
    return this.editors[elementId] || null
  }

  destroyEditor(elementId: string): void {
    if (this.editors[elementId]) {
      this.editors[elementId].destroy()
      delete this.editors[elementId]
    }
  }
}
